/**
 * 3D Room Visualizer — Touch Controller
 *
 * Adds mobile touch support to any viewer:
 * - Single finger drag → rotate (same as mouse drag)
 * - Two finger pinch → zoom (same as ctrl+scroll)
 *
 * Does not interfere with mouse/keyboard controls.
 */

import { applyDragRotation, applyZoom } from './cameraController';

interface TouchControllerOptions {
  /** Container element to attach touch listeners to */
  container: HTMLElement;
  /** Current rotation ref getter */
  getRotation: () => { x: number; y: number };
  /** Set rotation after drag */
  setRotation: (r: { x: number; y: number }) => void;
  /** Current camera FOV getter */
  getFov: () => number;
  /** Set camera FOV after pinch */
  setFov: (fov: number) => void;
  /** Called after any touch-driven change to re-render */
  onUpdate: () => void;
  /** Called when drag starts (for hasRotated state) */
  onDragStart?: () => void;
}

/**
 * Attach touch event listeners for rotate + pinch-zoom.
 * Returns a cleanup function to remove all listeners.
 */
export function attachTouchControls(options: TouchControllerOptions): () => void {
  const { container, getRotation, setRotation, getFov, setFov, onUpdate, onDragStart } = options;

  let isDragging = false;
  let previousTouch = { x: 0, y: 0 };
  let initialPinchDistance = 0;
  let initialFov = 50;

  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 1) {
      // Single finger — start drag rotation
      isDragging = true;
      previousTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      onDragStart?.();
    } else if (e.touches.length === 2) {
      // Two fingers — start pinch zoom
      isDragging = false;
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      initialPinchDistance = Math.sqrt(dx * dx + dy * dy);
      initialFov = getFov();
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault(); // Prevent page scroll while interacting with viewer

    if (e.touches.length === 1 && isDragging) {
      // Single finger drag → rotate
      const touch = e.touches[0];
      const deltaX = touch.clientX - previousTouch.x;
      const deltaY = touch.clientY - previousTouch.y;
      const newRotation = applyDragRotation(getRotation(), deltaX, deltaY);
      setRotation(newRotation);
      previousTouch = { x: touch.clientX, y: touch.clientY };
      onUpdate();
    } else if (e.touches.length === 2) {
      // Two finger pinch → zoom
      isDragging = false;
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      const currentDistance = Math.sqrt(dx * dx + dy * dy);

      if (initialPinchDistance > 0) {
        const scale = initialPinchDistance / currentDistance;
        // Convert pinch ratio to a FOV change — pinch in = zoom in (lower FOV)
        const targetFov = initialFov * scale;
        const clampedFov = Math.max(10, Math.min(120, targetFov));
        setFov(clampedFov);
        onUpdate();
      }
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (e.touches.length === 0) {
      isDragging = false;
    } else if (e.touches.length === 1) {
      // Went from pinch to single finger — reset drag origin
      isDragging = true;
      previousTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  container.addEventListener('touchstart', handleTouchStart, { passive: false });
  container.addEventListener('touchmove', handleTouchMove, { passive: false });
  container.addEventListener('touchend', handleTouchEnd, { passive: false });
  container.addEventListener('touchcancel', handleTouchEnd, { passive: false });

  return () => {
    container.removeEventListener('touchstart', handleTouchStart);
    container.removeEventListener('touchmove', handleTouchMove);
    container.removeEventListener('touchend', handleTouchEnd);
    container.removeEventListener('touchcancel', handleTouchEnd);
  };
}
